/**
 * src/views/focus/AssignedToMe.jsx — TICKET-209 P3
 *
 * "Assigned to You" section in the CEO Focus Dashboard. Reads focus_pins
 * where user_id = current user and renders a card per delegated boat/deal.
 * Opens the target via requestProjectOpen / requestDealOpen (race-free nav).
 *
 * Renders nothing when the list is empty (no visual noise in the default view).
 *
 * Conventions (src/components/CLAUDE.md):
 *   - Hooks at top of body
 *   - Tokens only, no raw hex
 *   - data-testid everywhere
 */

import { useState, useEffect } from 'react';
import { Anchor, ExternalLink } from 'lucide-react';
import { useT } from '../../lib/i18n/I18nContext';
import { fetchMyPins } from '../../lib/api/focusPins';
import { requestProjectOpen } from '../../lib/nav/projectNav';
import { requestDealOpen } from '../../lib/nav/dealNav';

export default function AssignedToMe() {
  const { t } = useT();
  const [pins, setPins] = useState([]);

  useEffect(() => {
    fetchMyPins().then(setPins).catch(() => {});
  }, []);

  if (pins.length === 0) return null;

  return (
    <div
      data-testid="assigned-to-me-section"
      style={{ margin: '16px 16px 0', padding: 16, borderRadius: 14, background: 'var(--glass)', border: '1px solid var(--bdr)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Anchor size={16} color="var(--accent)" />
        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)' }}>{t('assigned.sectionTitle')}</span>
        <span data-testid="assigned-count-badge" style={{ background: 'var(--sprob)', color: 'var(--accent)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99 }}>{pins.length}</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pins.map((pin) => {
          const name = pin.projects?.project_name || pin.crm_deals?.opportunity || '—';
          const vessel = pin.projects?.vessel || pin.crm_deals?.vessel_name || null;
          const delegatedBy = pin.pinned_by_profile?.display_name || '';

          const handleOpen = () => {
            if (pin.project_id) {
              requestProjectOpen(pin.project_id);
              window.dispatchEvent(new CustomEvent('acn:setActiveTab', { detail: 'projects' }));
            } else if (pin.deal_id) {
              requestDealOpen(pin.deal_id);
              window.dispatchEvent(new CustomEvent('acn:setActiveTab', { detail: 'pipeline' }));
            }
          };

          return (
            <div
              key={pin.id}
              data-testid={`assigned-pin-${pin.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 10,
                background: 'var(--card)', border: '1px solid var(--bdr)',
              }}
            >
              <Anchor size={14} color="var(--accent)" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {name}
                </div>
                {vessel && (
                  <div style={{ fontSize: 12, color: 'var(--tm)', marginTop: 1 }}>{vessel}</div>
                )}
                {delegatedBy && (
                  <div style={{ fontSize: 11, color: 'var(--tm)', marginTop: 2 }}>
                    {/* B3 fix: use t() substitution with {name} token instead of string concatenation */}
                    {t('delegate.delegatedBy', { name: delegatedBy })}
                    {pin.note ? ` — ${pin.note}` : ''}
                  </div>
                )}
              </div>
              <button
                type="button"
                data-testid={`assigned-open-${pin.id}`}
                onClick={handleOpen}
                style={{
                  minHeight: 44, minWidth: 44, padding: '8px 12px', borderRadius: 8,
                  background: 'var(--navy-surface)', color: 'var(--on-navy)',
                  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
                }}
              >
                <ExternalLink size={13} strokeWidth={2} />
                {t('assigned.openBoat')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
